import { type ReactElement } from 'react';
import { useBuildByIdQuery, useUpdateBuild } from '@/features/builds/hooks/builds';
import { DetailEditEntityDialog } from '@/features/shared/components/entityDetails/DetailEditEntityDialog';
import { useDetailEditEditor } from '@/features/shared/hooks/useDetailEditEditor';
import { formatStatusLabel } from '@/ui/primitives';
import type { PipelineRunSummaryItemDto } from '@flowqa/shared';

type PipelineRunEditDialogProps = {
  open: boolean;
  projectId: string;
  row: PipelineRunSummaryItemDto;
  rowName: string;
  onClose: () => void;
};

export const PipelineRunEditDialog = ({
  open,
  projectId,
  row,
  rowName,
  onClose,
}: PipelineRunEditDialogProps): ReactElement => {
  const updateBuild = useUpdateBuild();
  const buildQuery = useBuildByIdQuery(open ? row.id : null);
  const build = buildQuery.data;
  const startedAt = build?.startedAt ?? row.timestamp ?? null;
  const finishedAt =
    build?.finishedAt ??
    (row.timestamp && row.durationMs !== null
      ? new Date(row.timestamp.getTime() + row.durationMs)
      : null);
  const durationMs =
    !startedAt || !finishedAt
      ? row.durationMs
      : Math.max(finishedAt.getTime() - startedAt.getTime(), 0);
  const editEditor = useDetailEditEditor<PipelineRunSummaryItemDto['status']>({
    projectId: build?.projectId ?? projectId,
    successMessage: 'Pipeline run updated',
    saveEntity: async ({
      tags,
      status,
      timestamp,
      secondaryTimestamp,
      durationMs: nextDurationMs,
    }) => {
      const nextStartedAt = timestamp ? timestamp.toISOString() : null;
      const nextFinishedAt =
        secondaryTimestamp !== undefined
          ? (secondaryTimestamp?.toISOString() ?? null)
          : timestamp && nextDurationMs !== null && nextDurationMs !== undefined
            ? new Date(timestamp.getTime() + nextDurationMs).toISOString()
            : null;
      await updateBuild.mutateAsync({
        id: build?.id ?? row.id,
        tags,
        status,
        startedAt: nextStartedAt,
        finishedAt: nextFinishedAt,
      });
    },
  });

  return (
    <DetailEditEntityDialog
      open={open}
      entityLabel="Pipeline Run"
      entityName={rowName}
      projectId={build?.projectId ?? projectId}
      tags={build?.tags ?? row.tags ?? []}
      status={build?.status ?? row.status}
      statusOptions={[
        { value: 'queued', label: formatStatusLabel('queued') },
        { value: 'running', label: formatStatusLabel('running') },
        { value: 'completed', label: formatStatusLabel('completed') },
        { value: 'failed', label: formatStatusLabel('failed') },
      ]}
      timestamp={startedAt}
      secondaryTimestamp={finishedAt}
      durationMs={durationMs}
      timestampLabel="Started"
      secondaryTimestampLabel="Finished"
      durationLabel="Duration"
      durationInputMode="timeParts"
      loading={editEditor.isPending || updateBuild.isPending || buildQuery.isPending}
      cascadeCopy="Tags will be applied only to this pipeline run."
      onClose={() => {
        if (editEditor.isPending || updateBuild.isPending) return;
        onClose();
      }}
      onSave={async ({
        tags,
        colors,
        status,
        timestamp,
        secondaryTimestamp,
        durationMs: nextDurationMs,
      }) => {
        await editEditor.save({
          tags,
          colors,
          status,
          timestamp,
          secondaryTimestamp,
          durationMs: nextDurationMs,
        });
        onClose();
      }}
    />
  );
};
